import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppShell,
  Group,
  Text,
  Menu,
  Avatar,
  UnstyledButton,
  Box,
  Tabs,
  Burger,
  Drawer,
  Stack,
  NavLink as MantineNavLink,
  Image,
} from '@mantine/core';
import { useDisclosure, useElementSize } from '@mantine/hooks';
import { LogOut } from 'react-feather';
import { useAuthStore } from '../../stores/auth.js';
import { ImpersonationBanner } from '../ImpersonationBanner.js';
import { CohortSwitcher } from '../CohortSwitcher.js';
import { useSelectedCohortAccess } from '../../stores/participantCohort.js';
import { FeaturesDisabledPage } from '../../pages/FeaturesDisabledPage.js';
import classes from './ParticipantLayout.module.css';
import medleyLogoWhite from '../../assets/medley-logo-white.png';
import { AppVersion } from '../common/AppVersion';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const PARTICIPANT_NAV_ITEMS = [
  { path: '/participant', label: 'Dashboard' },
];

export function ParticipantLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure(false);
  // MED-1307 (MDF-184): master toggle off wins over app access off.
  const cohortAccess = useSelectedCohortAccess();
  // MED-1383: a participant must never see "Medley Admin" in the tab.
  useDocumentTitle('Medley');

  const handleLogout = async () => {
    await logout();
    navigate('/participant/login');
  };

  const userInitials = user?.initials || user?.first_name?.charAt(0) || 'P';
  const userName = user?.display_name || `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || 'Participant';
  const userTitle = user?.job_title || '';

  const handleMobileNavClick = (path: string) => {
    navigate(path);
    closeMobileNav();
  };

  const isPathActive = (path: string) => {
    if (path === '/participant') {
      return location.pathname === '/participant';
    }
    return location.pathname.startsWith(path);
  };

  // Measure the banner, because its height changes with its text and the
  // screen width. A fixed height let the page cover the nav (MDF-251).
  const { ref: bannerRef, height: bannerHeight } = useElementSize();
  const headerHeight = 60 + bannerHeight;

  return (
    <AppShell header={{ height: headerHeight }} padding={0}>
      <AppShell.Header className={classes.header} style={{ height: headerHeight }}>
        <div ref={bannerRef}>
          <ImpersonationBanner />
        </div>
        <Group h={60} px="md" justify="space-between">
          {/* Left: Burger (mobile), Logo, Navigation */}
          <Group gap="md">
            <Burger
              opened={mobileNavOpened}
              onClick={toggleMobileNav}
              hiddenFrom="md"
              size="sm"
              color="white"
            />

            <Group gap="xs" className={classes.logo} onClick={() => navigate('/participant')}>
              <Image src={medleyLogoWhite} alt="Medley" h={24} w="auto" fit="contain" />
            </Group>

            <Tabs
              variant="pills"
              classNames={{
                root: classes.tabsRoot,
                list: classes.tabsList,
                tab: classes.tab,
              }}
              value={location.pathname}
              visibleFrom="md"
            >
              <Tabs.List>
                {PARTICIPANT_NAV_ITEMS.map(item => (
                  <NavLink key={item.path} to={item.path} className={classes.navLink}>
                    {() => (
                      <Tabs.Tab value={item.path} data-active={isPathActive(item.path) || undefined}>
                        {item.label}
                      </Tabs.Tab>
                    )}
                  </NavLink>
                ))}
              </Tabs.List>
            </Tabs>
          </Group>

          {/* Right: Cohort Switcher + User Menu */}
          <Group gap="sm">
          <CohortSwitcher />
          <Menu shadow="md" width={200} position="bottom-end">
            <Menu.Target>
              <UnstyledButton className={classes.userButton}>
                <Group gap="sm">
                  <Avatar
                    size="sm"
                    radius="xl"
                    color="medleyGreen"
                    src={user?.avatar_url}
                  >
                    {userInitials}
                  </Avatar>
                  <Box visibleFrom="sm">
                    <Text size="sm" fw={500} c="white" lh={1.2}>
                      {userName}
                    </Text>
                    {userTitle && (
                      <Text size="xs" c="rgba(255,255,255,0.7)" lh={1.2}>
                        {userTitle}
                      </Text>
                    )}
                  </Box>
                </Group>
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>Account</Menu.Label>
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<LogOut size={14} />}
                onClick={handleLogout}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Mobile Navigation Drawer */}
      <Drawer
        opened={mobileNavOpened}
        onClose={closeMobileNav}
        size="xs"
        padding="md"
        title={
          <Image
            src={medleyLogoWhite}
            alt="Medley"
            h={20}
            w="auto"
            fit="contain"
            style={{ filter: 'invert(1) brightness(0.3)' }}
          />
        }
        hiddenFrom="md"
        zIndex={1000}
      >
        <Stack gap="xs">
          {PARTICIPANT_NAV_ITEMS.map(item => (
            <MantineNavLink
              key={item.path}
              label={item.label}
              active={isPathActive(item.path)}
              onClick={() => handleMobileNavClick(item.path)}
              className={classes.mobileNavLink}
            />
          ))}
        </Stack>
      </Drawer>

      <AppShell.Main className={classes.main}>
        {cohortAccess === 'enabled' ? <Outlet /> : <FeaturesDisabledPage reason={cohortAccess} />}
      </AppShell.Main>

      {/* MED-1360: the version line follows the page content, so it never
          covers it. AppVersion hides it where it would add a scroll. */}
      <AppVersion />
    </AppShell>
  );
}

export default ParticipantLayout;
